import React from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import CreatorProgramTable from '../components/CreatorProgramTable';
import { METRIC_LABELS } from '../constants';
import {
  EMPTY_VALUE,
  formatCount,
  formatCurrency,
  formatPercentage,
} from '../utils/formatters';
import { SectionCard, SectionTitle } from '../Styles';
import SummaryDetailPanel from './SummaryDetailPanel';
import type { BreakdownRow, BreakdownVariant } from '../types';

interface BreakdownTableProps {
  title: string;
  /** Which flavour of breakdown — decides the label column only. */
  variant: BreakdownVariant;
  rows?: BreakdownRow[];
  loading?: boolean;
  emptyText: string;
}

/**
 * How each flavour names its rows.
 *
 * Keyed off the caller's `variant` rather than sniffed from the row: the API sends
 * every key on every row and **nulls** the ones that don't apply (a `byCode` row
 * carries `storeId: null`, not a missing `storeId`), so any `!== undefined` check
 * misreads code rows as store rows. The parent always knows which list it passed,
 * so there is nothing to infer.
 */
const ROW_LABEL: Record<BreakdownVariant, (row: BreakdownRow) => string> = {
  store: (row) => row.storeName || row.storeId || EMPTY_VALUE,
  code: (row) => row.code || row.codeId || EMPTY_VALUE,
};

const ROW_KEY: Record<BreakdownVariant, (row: BreakdownRow) => string> = {
  store: (row) => row.storeId || row.storeName || '',
  code: (row) => row.codeId || row.code || '',
};

/**
 * Per-store and per-code breakdown.
 *
 * `byStore[]` and `byCode[]` rows carry the exact same metric shape as `summary`
 * (docs §3.2), so one component serves both — only the label column differs.
 *
 * The table shows the headline figures; expanding a row reveals the full metric set
 * (customer mix, refunds, window-scoped earnings) so the table stays readable without
 * dropping any of the data the docs asked to be surfaced.
 */
const BreakdownTable: React.FC<BreakdownTableProps> = ({
  title,
  variant,
  rows,
  loading,
  emptyText,
}) => {
  const data = rows ?? [];

  const columns: ColumnsType<BreakdownRow> = [
    {
      title: variant === 'store' ? 'STORE' : 'CODE',
      key: 'label',
      fixed: 'left',
      width: 180,
      render: (_, record) => {
        const label = ROW_LABEL[variant](record);
        return variant === 'code' ? <Tag>{label}</Tag> : label;
      },
    },
    {
      title: METRIC_LABELS.ORDERS.toUpperCase(),
      key: 'orders',
      width: 90,
      render: (_, record) => formatCount(record.orders),
    },
    {
      title: 'GROSS SUBTOTAL',
      key: 'grossSubtotal',
      width: 140,
      render: (_, record) => formatCurrency(record.grossSubtotal),
    },
    {
      title: 'COMMISSION BASE',
      key: 'commissionBase',
      width: 150,
      render: (_, record) => formatCurrency(record.commissionBase),
    },
    {
      // Net, not the raw `customerDiscount` — see §3.2.
      title: 'CUSTOMER DISCOUNT',
      key: 'netCustomerDiscount',
      width: 160,
      render: (_, record) => formatCurrency(record.netCustomerDiscount),
    },
    {
      title: 'CREATOR EARNINGS',
      key: 'netCreatorEarnings',
      width: 155,
      render: (_, record) => (
        <strong>{formatCurrency(record.netCreatorEarnings)}</strong>
      ),
    },
    {
      title: 'TOTAL COST',
      key: 'totalCost',
      width: 120,
      render: (_, record) => formatCurrency(record.totalCost),
    },
    {
      title: 'COST % OF BASE',
      key: 'costPercentOfBase',
      width: 140,
      render: (_, record) => formatPercentage(record.costPercentOfBase),
    },
    {
      title: 'AOV',
      key: 'aov',
      width: 110,
      render: (_, record) => formatCurrency(record.aov),
    },
  ];

  const expandedRowRender = (record: BreakdownRow) => (
    <SummaryDetailPanel summary={record} />
  );

  return (
    <SectionCard>
      <SectionTitle>{title}</SectionTitle>
      <CreatorProgramTable<BreakdownRow>
        variant="nested"
        columns={columns}
        dataSource={data}
        rowKey={ROW_KEY[variant]}
        loading={loading}
        emptyText={emptyText}
        expandable={{ expandedRowRender }}
      />
    </SectionCard>
  );
};

/**
 * Memoised: re-renders only when its own props change, not whenever the parent
 * screen re-renders for an unrelated filter or fetch.
 */
export default React.memo(BreakdownTable);
